import { SelectionViewModel } from 'src_common/common/websocket2/models/SelectionModel/SelectionViewModel';
import { BetSlipFilter, BetSlipState } from 'src/domains/sportsbook/betting/state/BetSlipState';
import { computed, makeObservable } from 'mobx';
import { SdkCustomer } from 'src/domains/layouts/state/customer';
import { SelectionId } from 'src_common/common/websocket2/id/WebsocketId';

interface PropsType {
    selectionId: SelectionId | undefined;
    sp?: boolean;
    forceSuspended?: boolean;
}

export class SelectionState {
    public constructor(
        private readonly betSlipState: BetSlipState,
        private readonly props: PropsType,
        private readonly sdkCustomer: SdkCustomer
    ) {
        makeObservable(this);
    }

    @computed private get selectionId(): SelectionId | null {
        return this.props.selectionId ?? null;
    }

    @computed private get isAlwaysSP(): boolean | null {
        return this.props.sp ?? this.isSPSport;
    }

    @computed public get selectionView(): SelectionViewModel | null {
        if (this.selectionId !== null) {
            const selectionModel = this.selectionId.getModel();

            if (selectionModel !== null) {
                if (selectionModel.templateId === 'unnamed-favourite' && selectionModel.isSP) {
                    return selectionModel.forView(true);
                }
                return selectionModel.forView(this.isAlwaysSP ?? false);
            }
        }

        return null;
    }

    @computed public get isSelected(): boolean {
        const { isReferred } = this.betSlipState.referralState;

        if (isReferred) return false;

        if (this.selectionId !== null) {
            const legModel = this.betSlipState.ids.getSingleId(this.selectionId, 0).getModel();
            const isSelected = this.betSlipState.isSelected(this.selectionId);
            const selection = isSelected ? legModel : null;
            if (selection !== null && this.selectionView !== null) {
                const { priceType } = selection;

                if (this.isAlwaysSP === true && priceType !== 'sp') {
                    return false;
                }

                return true;
            }
        }

        return false;
    }

    @computed public get getOddsValue(): React.ReactNode {
        if (this.selectionView !== null) {
            const { displayPrice } = this.selectionView;

            if (this.selectionId !== null) {
                const legModel = this.betSlipState.ids.getSingleId(this.selectionId, 0).getModel();
                const isSelected = this.betSlipState.isSelected(this.selectionId);
                const selection = isSelected ? legModel : null;
                if (selection !== null) {
                    return selection.isSelectedSP ? 'SP' : displayPrice;
                }
            }

            return displayPrice;
        }

        return '-';
    }

    @computed public get isSPSport(): boolean {
        const basicData = this.sdkCustomer.basicData.get();
        const eventId = this.props.selectionId?.getEventId() ?? null;

        if (basicData.type === 'ready' && eventId !== null) {
            const eventModel = eventId.getEventModel();
            const selectionSport = eventModel?.sportOriginal ?? '';
            return basicData.value?.spOnlySports?.includes(selectionSport) ?? false;
        }

        return false;
    }

    @computed public get forceSuspended(): boolean {
        return this.props.forceSuspended === true;
    }

    public chooseNew = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>): void => {
        const { selectedTab, changeBetslipTab, referralState, betSlipSummaryState, legsState, ids } = this.betSlipState;
        const { isReferred } = referralState;
        const { clearError } = betSlipSummaryState;
        e.preventDefault();

        if (this.forceSuspended) {
            return;
        }

        if (this.selectionView === null) {
            return;
        }

        const { displayPrice } = this.selectionView;
        const sp = displayPrice === 'SP';

        if (this.selectionId !== null && isReferred === false) {
            const selection = ids.getSingleId(this.selectionId, 0).getModel();

            if (selection.isSelectedSP && this.isAlwaysSP === null) {
                selection.removeLeg();
            } else if (this.isAlwaysSP === true || sp) {
                legsState.betslipData.onAddSPBet(this.selectionId);
            } else {
                legsState.betslipData.onAddSimpleBet(this.selectionId);
            }
            if (selectedTab !== BetSlipFilter.BETSLIP) {
                changeBetslipTab(BetSlipFilter.BETSLIP);
            }

            clearError();
        }
    };
}
